import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", message: "", website: "" };
const cls = "h-12 rounded-xl bg-white border-slate-300 text-base focus-visible:ring-[var(--green)]";

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null, website: form.website || null });
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details and try again, or call 07836 207134.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-[1.5rem] bg-[var(--green-tint)] p-8" data-testid="contact-form-success" role="status">
        <CheckCircle2 className="h-10 w-10 text-[var(--green)]" />
        <h3 className="mt-4 font-display text-2xl font-bold text-[var(--navy)]">Message sent – thank you!</h3>
        <p className="mt-2 text-[var(--ink)]">We'll get back to you as soon as we can.</p>
        <button type="button" onClick={() => setSent(false)} data-testid="contact-form-reset" className="mt-6 font-semibold text-[var(--navy)] underline">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
      <input type="text" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="space-y-2"><Label htmlFor="c-name">Name *</Label><Input id="c-name" required minLength={2} value={form.name} onChange={set("name")} className={cls} data-testid="contact-form-name-input" /></div>
      <div className="space-y-2"><Label htmlFor="c-email">Email *</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} className={cls} data-testid="contact-form-email-input" /></div>
      <div className="space-y-2 sm:col-span-2"><Label htmlFor="c-phone">Phone (optional)</Label><Input id="c-phone" type="tel" value={form.phone} onChange={set("phone")} className={cls} data-testid="contact-form-phone-input" /></div>
      <div className="space-y-2 sm:col-span-2"><Label htmlFor="c-msg">Message *</Label><Textarea id="c-msg" required minLength={5} rows={5} value={form.message} onChange={set("message")} className="rounded-xl bg-white border-slate-300 text-base" data-testid="contact-form-message-textarea" /></div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} data-testid="contact-form-submit-button"
          className="inline-flex items-center gap-3 rounded-full bg-[var(--navy)] px-8 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-70">
          {loading && <Loader2 className="h-5 w-5 animate-spin" />} {loading ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
};
